import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primaryNavItems = [
    { path: '/homepage', label: 'Home', icon: 'Home' },
    { path: '/portfolio', label: 'Portfolio', icon: 'Briefcase' },
    { path: '/crypto-dashboard', label: 'Crypto Dashboard', icon: 'TrendingUp' },
    { path: '/news', label: 'News', icon: 'Newspaper' },
    { path: '/legal-hub', label: 'Legal Hub', icon: 'Scale' },
    { path: '/about', label: 'About', icon: 'Users' },
    { path: '/contact', label: 'Contact', icon: 'MessageCircle' }
  ];

  const secondaryNavItems = [
    // Empty array since we moved items to primary navigation
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-header transition-brand ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md shadow-brand border-b border-border' 
          : 'bg-background/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Logo */}
          <Link 
            to="/homepage" 
            className="flex items-center space-x-3 hover-lift focus-brand"
            onClick={closeMenu}
          >
            <div className="relative">
              <div className="w-12 h-12 rounded-brand bg-gradient-to-br from-primary via-accent to-primary flex items-center justify-center shadow-brand border-2 border-white/20">
                <span className="text-white font-bold text-xl font-mono tracking-wider">REX</span>
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-success to-accent rounded-full animate-pulse shadow-sm"></div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-2xl font-bold text-gradient-brand tracking-wide">REXFEL</h1>
              <p className="text-xs text-muted-foreground font-semibold tracking-wider uppercase">Technology and Innovation</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-brand-sm transition-brand hover:bg-muted focus-brand ${
                  isActivePath(item?.path)
                    ? 'bg-primary/10 text-primary border border-primary/20' :'text-foreground hover:text-primary'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span className="font-medium">{item?.label}</span>
              </Link>
            ))}

            {/* More Menu - Hidden since secondaryNavItems is empty */}
            {secondaryNavItems?.length > 0 && (
              <div className="relative group">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 px-4 py-2"
                >
                  <Icon name="MoreHorizontal" size={16} />
                  <span className="font-medium">More</span>
                  <Icon name="ChevronDown" size={14} />
                </Button>
                
                <div className="absolute top-full right-0 mt-2 w-56 bg-popover border border-border rounded-brand shadow-brand-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-brand z-50">
                  <div className="py-2">
                    {secondaryNavItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center space-x-3 px-4 py-3 text-sm transition-brand hover:bg-muted focus-brand ${
                          isActivePath(item?.path)
                            ? 'bg-primary/10 text-primary border-r-2 border-primary' :'text-foreground hover:text-primary'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} />
                        <span className="font-medium">{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={toggleMenu}
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-brand overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-6 py-4 bg-card border-t border-border">
            <nav className="space-y-2">
              {[...primaryNavItems, ...secondaryNavItems]?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-brand-sm transition-brand ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-brand-sm'
                      : 'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile CTAs */}
            <div className="mt-6 space-y-3">
              
              <Button
                variant="default"
                fullWidth
                className="justify-center bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                onClick={closeMenu}
              >
                <Icon name="ArrowRight" size={16} />
                <span className="ml-2">Get Started</span>
              </Button>
            </div>

            {/* Mobile Contact Info */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <a 
                  href="mailto:hello@rexfel.com" 
                  className="flex items-center space-x-2 hover:text-primary transition-brand"
                >
                  <Icon name="Mail" size={16} />
                  <span>hello@rexfel.com</span>
                </a>
                <a 
                  href="tel:+49-40-123456" 
                  className="flex items-center space-x-2 hover:text-primary transition-brand"
                >
                  <Icon name="Phone" size={16} />
                  <span>+49 40 123456</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;